// Integrated Messaging Component - Database-backed cross-browser messaging
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Send, Users, MessageCircle, Search, MinusCircle, GripVertical, Trash2, Paperclip, Image as ImageIcon, Smile, UserPlus, Check, XCircle, Bell, ArrowLeft } from 'lucide-react';
import * as MessagingAPI from '@/lib/messaging-api';
import * as GroupChatAPI from '@/lib/group-chat-api';
import * as ContactRequestAPI from '@/lib/contact-request-api';
import { PushNotificationService } from '@/services/pushNotifications';

const INLINE_EMOJIS = ['😀','😂','😊','😍','🥳','🤔','👍','👏','🙌','🔥','❤️','🎉','✅','💯','🙏','🤝'];

interface Message {
  id: string;
  from_user_id: string;
  to_user_id: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface OnlineUser {
  id: string;
  email: string;
  name: string;
  is_online: boolean;
  show_online_status: boolean;
  last_active_at: string;
}

interface IntegratedMessagingProps {
  currentUser: { id: string; name: string; email: string };
  onClose: () => void;
  isMinimized: boolean;
  onMinimize: () => void;
}

export function IntegratedMessaging({ currentUser, onClose, isMinimized, onMinimize }: IntegratedMessagingProps) {
  const [activeTab, setActiveTab] = useState<'contacts' | 'groups' | 'browse'>('contacts');
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [contacts, setContacts] = useState<OnlineUser[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<any[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<any[]>([]);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<OnlineUser[]>([]);
  const [addContactSearch, setAddContactSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<OnlineUser | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [unreadCounts, setUnreadCounts] = useState<{[userId: string]: number}>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [position, setPosition] = useState({ x: window.innerWidth - 820, y: 20 });
  const [size, setSize] = useState({ width: 800, height: 600 });
  const [minimizedSize, setMinimizedSize] = useState({ width: 280, height: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string>('');
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showBrowseGroupsModal, setShowBrowseGroupsModal] = useState(false);
  const [allGroups, setAllGroups] = useState<GroupChatAPI.Group[]>([]);
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [groups, setGroups] = useState<GroupChatAPI.Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GroupChatAPI.Group | null>(null);
  const [groupMessages, setGroupMessages] = useState<GroupChatAPI.GroupMessage[]>([]);
  const [groupMembers, setGroupMembers] = useState<GroupChatAPI.GroupMember[]>([]);
  const [joinRequests, setJoinRequests] = useState<GroupChatAPI.JoinRequest[]>([]);
  // Admin messages removed - no longer needed
  
  // Mobile-specific states
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-show chat on mobile when user/group selected
  useEffect(() => {
    if (isMobile && (selectedUser || selectedGroup)) {
      setShowChatOnMobile(true);
    }
  }, [selectedUser, selectedGroup, isMobile]);

  // Expose active conversation for OrganizerPage to mark-as-read per-thread without clearing all unread
  useEffect(() => {
    (window as any).__whiptMessagingState = {
      activeUserId: selectedUser?.id || null,
      activeGroupId: selectedGroup?.id || null,
    };
    return () => {
      if ((window as any).__whiptMessagingState) {
        delete (window as any).__whiptMessagingState;
      }
    };
  }, [selectedUser?.id, selectedGroup?.id]);

  // Ensure message bodies are always strings to avoid React render errors
  const normalizeText = (value: any) => {
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value ?? '');
    }
  };

  // Admin user lookup removed - no longer needed

  // Load saved position
  useEffect(() => {
    const savedPosition = localStorage.getItem('whipt_messaging_position');
    if (savedPosition) {
      setPosition(JSON.parse(savedPosition));
    }
    const savedMinimizedSize = localStorage.getItem('whipt_messaging_minimized_size');
    if (savedMinimizedSize) {
      setMinimizedSize(JSON.parse(savedMinimizedSize));
    }
  }, []);

  // Save position when it changes
  useEffect(() => {
    localStorage.setItem('whipt_messaging_position', JSON.stringify(position));
  }, [position]);

  // Save minimized size when it changes
  useEffect(() => {
    localStorage.setItem('whipt_messaging_minimized_size', JSON.stringify(minimizedSize));
  }, [minimizedSize]);

  // Handle drag
  const handleDragStart = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  useEffect(() => {
    const handleDragMove = (e: MouseEvent) => {
      if (!isDragging) return;
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    };

    const handleDragEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, dragOffset]);

  // Handle resize
  const handleResizeStart = (e: React.MouseEvent, direction: string) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
  };

  useEffect(() => {
    const handleResizeMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      if (isMinimized) {
        // Resize minimized window
        const newMinSize = { ...minimizedSize };
        
        if (resizeDirection.includes('e')) {
          newMinSize.width = Math.max(220, e.clientX - position.x);
        }
        if (resizeDirection.includes('s')) {
          newMinSize.height = Math.max(150, e.clientY - position.y);
        }
        
        setMinimizedSize(newMinSize);
      } else {
        // Resize full window
        const newSize = { ...size };
        const newPosition = { ...position };

        if (resizeDirection.includes('e')) {
          newSize.width = Math.max(600, e.clientX - position.x);
        }
        if (resizeDirection.includes('w')) {
          const newWidth = Math.max(600, position.x + size.width - e.clientX);
          newPosition.x = position.x + size.width - newWidth;
          newSize.width = newWidth;
        }
        if (resizeDirection.includes('s')) {
          newSize.height = Math.max(400, e.clientY - position.y);
        }
        if (resizeDirection.includes('n')) {
          const newHeight = Math.max(400, position.y + size.height - e.clientY);
          newPosition.y = position.y + size.height - newHeight;
          newSize.height = newHeight;
        }

        setSize(newSize);
        setPosition(newPosition);
      }
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
      setResizeDirection('');
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, resizeDirection, size, position, isMinimized, minimizedSize]);

  // Request push notification permission on mount
  useEffect(() => {
    if (PushNotificationService.isNotificationSupported()) {
      PushNotificationService.requestPermission().then(permission => {
        console.log('[Messaging] Push notification permission:', permission);
      });
    }
    // Note: Removed body scroll lock on mobile as it blocks keyboard
  }, []);

  // Load contacts from database
  useEffect(() => {
    const loadContacts = async () => {
      console.log('[Messaging] Loading contacts from database...');
      
      // Update current user's last active timestamp
      await MessagingAPI.updateLastActive(currentUser.id);
      
      // Get user's contacts list
      const userContacts = await MessagingAPI.getUserContacts(currentUser.id);
      
      // Filter out system admin from non-admins
      const isAdmin = (currentUser.role && String(currentUser.role).toLowerCase() === 'admin') || currentUser.email === 'system@whipt.app';
      const filteredContacts = userContacts.filter(u => {
        if (!isAdmin && (u.email === 'system@whipt.app' || u.name === 'WHIPT Admin')) return false;
        return true;
      });
      setContacts(filteredContacts);
      setOnlineUsers(filteredContacts); // Keep onlineUsers for compatibility
      
      console.log('[Messaging] Loaded contacts:', filteredContacts.map(u => `${u.name} (${u.email})`).join(', '));
      
      // Update unread counts and trigger notifications
      const counts: {[userId: string]: number} = {};
      for (const user of filteredContacts) {
        const count = await MessagingAPI.getUnreadCount(currentUser.id, user.id);
        if (count > 0) {
          counts[user.id] = count;
          // Check if this is a NEW unread message (count increased)
          const previousCount = unreadCounts[user.id] || 0;
          if (count > previousCount) {
            // Fetch the latest message for preview
            const recentMessages = await MessagingAPI.getConversationMessages(currentUser.id, user.id);
            const latestMsg = recentMessages[recentMessages.length - 1];
            const preview = latestMsg?.message?.substring(0, 100) || 'New message';
            
            console.log('[Messaging] 📱 NEW MESSAGE - Showing persistent notification:', user.name, preview);
            // Use persistent notification (stays longer, works when locked)
            PushNotificationService.showMessageNotificationPersistent(user.name, preview);
          }
        }
      }
      setUnreadCounts(counts);
    };
    
    loadContacts();
    const interval = setInterval(loadContacts, 30000); // Reduced to 30 seconds to prevent flickering
    
    return () => clearInterval(interval);
  }, [currentUser.id]);

  // Load groups
  useEffect(() => {
    const loadGroups = async () => {
      console.log('[Messaging] Loading groups for user:', currentUser.id);
      try {
        const userGroups = await GroupChatAPI.getUserGroups(currentUser.id);
        console.log('[Messaging] ✅ Loaded groups:', userGroups.length, userGroups);
        setGroups(userGroups);
      } catch (error) {
        console.error('[Messaging] ❌ Failed to load groups:', error);
      }
    };
    
    loadGroups();
    const interval = setInterval(loadGroups, 5000); // Refresh every 5 seconds
    
    return () => clearInterval(interval);
  }, [currentUser.id]);

  // Load join requests for groups you created
  useEffect(() => {
    const loadJoinRequests = async () => {
      try {
        const requests = await GroupChatAPI.getPendingJoinRequests(currentUser.id);
        setJoinRequests(requests);
      } catch (error) {
        console.error('[Messaging] Failed to load join requests:', error);
      }
    };
    
    loadJoinRequests();
    const interval = setInterval(loadJoinRequests, 5000);
    
    return () => clearInterval(interval);
  }, [currentUser.id]);

  // Load contact requests (friend requests)
  useEffect(() => {
    const loadContactRequests = async () => {
      try {
        const incoming = await ContactRequestAPI.getIncomingRequests(currentUser.id);
        const outgoing = await ContactRequestAPI.getOutgoingRequests(currentUser.id);
        setIncomingRequests(incoming);
        setOutgoingRequests(outgoing);
        console.log('[Messaging] Loaded contact requests:', incoming.length, 'incoming,', outgoing.length, 'outgoing');
      } catch (error) {
        console.error('[Messaging] Failed to load contact requests:', error);
      }
    };
    
    loadContactRequests();
    const interval = setInterval(loadContactRequests, 15000); // Every 15 seconds
    
    return () => clearInterval(interval);
  }, [currentUser.id]);

  // Load approved contacts only
  useEffect(() => {
    const loadApprovedContacts = async () => {
      try {
        const approved = await ContactRequestAPI.getApprovedContacts(currentUser.id);
        setContacts(approved as any);
        console.log('[Messaging] Loaded', approved.length, 'approved contacts');
      } catch (error) {
        console.error('[Messaging] Failed to load approved contacts:', error);
      }
    };
    
    loadApprovedContacts();
    const interval = setInterval(loadApprovedContacts, 30000); // Every 30 seconds instead of 5
    
    return () => clearInterval(interval);
  }, [currentUser.id]);

  // Load all users for browsing
  useEffect(() => {
    const loadAllUsers = async () => {
      try {
        const users = await ContactRequestAPI.getAllUsers(currentUser.id);
        setAllUsers(users);
        console.log('[Messaging] Loaded', users.length, 'users for browsing');
      } catch (error) {
        console.error('[Messaging] Failed to load all users:', error);
      }
    };
    
    loadAllUsers();
    const interval = setInterval(loadAllUsers, 60000); // Every 60 seconds
    
    return () => clearInterval(interval);
  }, [currentUser.id]);

  // Filter contacts to exclude users who are ONLY in groups (prevent duplicates)
  // Show in People tab: users you've explicitly added as contacts OR have direct message history
  const peopleTabContacts = useMemo(() => {
    return contacts; // All contacts are shown - these are explicitly added via "Add Contact"
  }, [contacts]);

  // Update selectedUser status when onlineUsers changes
  useEffect(() => {
    if (selectedUser) {
      const updatedUser = onlineUsers.find(u => u.id === selectedUser.id);
      if (updatedUser) {
        // User is still online, update their info
        setSelectedUser(updatedUser);
      } else {
        // User went offline, update selectedUser to show offline status
        setSelectedUser(prev => prev ? { ...prev, show_online_status: false, is_online: false } : null);
      }
    }
  }, [onlineUsers]);

  // Load messages when user is selected
  useEffect(() => {
    if (!selectedUser) return;

    const loadMessages = async () => {
      console.log('[Messaging] Loading conversation with:', selectedUser.name);
      const msgs = await MessagingAPI.getConversationMessages(currentUser.id, selectedUser.id);
      setMessages(msgs);
      
      // Mark messages as read
      await MessagingAPI.markMessagesAsRead(currentUser.id, selectedUser.id);
      
      // Clear unread count for this user
      setUnreadCounts(prev => {
        const updated = { ...prev };
        delete updated[selectedUser.id];
        return updated;
      });
    };

    loadMessages();
    const interval = setInterval(loadMessages, 1000); // Poll every second

    return () => clearInterval(interval);
  }, [selectedUser?.id, currentUser.id]);

  // Admin notifications removed - no longer needed

  // Load group messages and members when group is selected
  useEffect(() => {
    if (!selectedGroup) return;

    const loadGroupMessages = async () => {
      console.log('[Messaging] Loading group messages:', selectedGroup.name);
      const msgs = await GroupChatAPI.getGroupMessages(selectedGroup.id);
      setGroupMessages(msgs);
    };

    const loadGroupMembers = async () => {
      const members = await GroupChatAPI.getGroupMembers(selectedGroup.id);
      setGroupMembers(members);
      console.log('[Messaging] Loaded group members:', members.length);
    };

    loadGroupMessages();
    loadGroupMembers();
    const interval = setInterval(loadGroupMessages, 1000); // Poll every second

    return () => clearInterval(interval);
  }, [selectedGroup?.id]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Contact Management Functions
  const loadAvailableUsers = async () => {
    console.log('[Messaging] Loading available users for adding...');
    const available = await MessagingAPI.getAvailableUsers(currentUser.id);
    // Filter out system admin from non-admins
    const isAdmin = (currentUser.role && String(currentUser.role).toLowerCase() === 'admin') || currentUser.email === 'system@whipt.app';
    const filtered = available.filter(u => {
      if (!isAdmin && (u.email === 'system@whipt.app' || u.name === 'WHIPT Admin')) return false;
      return true;
    });
    setAvailableUsers(filtered);
  };

  const handleAddContact = async (contactUserId: string) => {
    const result = await MessagingAPI.addContact(currentUser.id, contactUserId);
    if (result.success) {
      console.log('✅ Contact added successfully');
      // Reload contacts
      const userContacts = await MessagingAPI.getUserContacts(currentUser.id);
      setContacts(userContacts);
      setOnlineUsers(userContacts);
      // Reload available users
      await loadAvailableUsers();
    } else {
      alert(result.error || 'Failed to add contact');
    }
  };

  const handleRemoveContact = async (contactUserId: string) => {
    if (!confirm('Remove this contact?')) return;
    const result = await MessagingAPI.removeContact(currentUser.id, contactUserId);
    if (result.success) {
      console.log('✅ Contact removed successfully');
      // Reload contacts
      const userContacts = await MessagingAPI.getUserContacts(currentUser.id);
      setContacts(userContacts);
      setOnlineUsers(userContacts);
      // Clear selection if removed user was selected
      if (selectedUser?.id === contactUserId) {
        setSelectedUser(null);
      }
    }
  };

  const openAddContactModal = () => {
    loadAvailableUsers();
    setShowAddContactModal(true);
  };


  const filteredUsers = onlineUsers.filter(user => {
    const isAdmin = (currentUser.role && String(currentUser.role).toLowerCase() === 'admin') || currentUser.email === 'system@whipt.app';
    if (!isAdmin && (user.email === 'system@whipt.app' || user.name === 'WHIPT Admin')) {
      return false;
    }
    const q = searchQuery.toLowerCase();
    return user.name.toLowerCase().includes(q) || user.email.toLowerCase().includes(q);
  });

  // Minimized state - Floating draggable and resizable mini window
  if (isMinimized) {
    const totalUnread = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);
    
    return (
      <div
        style={{ 
          left: position.x, 
          top: position.y,
          width: minimizedSize.width,
          height: minimizedSize.height
        }}
        className="fixed z-[1100] bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-700 text-white rounded-2xl shadow-2xl hover:shadow-purple-500/50 transition-shadow select-none border-2 border-purple-400/50 flex flex-col"
      >
        {/* Resize Handles for Minimized State */}
        <div 
          onMouseDown={(e) => {
            setIsResizing(true);
            setResizeDirection('s');
            e.stopPropagation();
          }}
          className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-purple-400/30 transition-colors rounded-b-2xl"
        />
        <div 
          onMouseDown={(e) => {
            setIsResizing(true);
            setResizeDirection('e');
            e.stopPropagation();
          }}
          className="absolute top-0 bottom-0 right-0 w-2 cursor-ew-resize hover:bg-purple-400/30 transition-colors rounded-r-2xl"
        />
        <div 
          onMouseDown={(e) => {
            setIsResizing(true);
            setResizeDirection('se');
            e.stopPropagation();
          }}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize hover:bg-purple-400/50 transition-colors rounded-br-2xl"
        />

        {/* Drag Handle Header */}
        <div
          onMouseDown={handleDragStart}
          className="px-4 py-3 cursor-move bg-purple-700/30 rounded-t-2xl border-b border-purple-400/30 hover:bg-purple-700/50 transition-colors flex-shrink-0"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <GripVertical className="w-4 h-4 opacity-50" />
              <MessageCircle className="w-5 h-5" />
              <span className="font-bold text-sm">Messages</span>
              {totalUnread > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                  {totalUnread}
                </span>
              )}
            </div>
            <button
              onClick={onMinimize}
              className="hover:bg-white/20 p-1 rounded transition-colors"
              title="Expand"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Minimized Content Preview - Scrollable */}
        <div className="px-4 py-3 space-y-2 flex-1 overflow-y-auto">
          {selectedUser && (
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="font-medium truncate">
                Chatting with {selectedUser.name}
              </span>
            </div>
          )}
          {selectedGroup && (
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4" />
              <span className="font-medium truncate">
                {selectedGroup.name}
              </span>
            </div>
          )}
          {!selectedUser && !selectedGroup && (
            <div className="text-sm opacity-80">
              Click to open messaging
            </div>
          )}
          {/* Show recent messages if any */}
          {selectedUser && messages.length > 0 && (
            <div className="mt-2 space-y-1 text-xs opacity-90">
              {messages.slice(-3).map((msg, idx) => (
                <div key={idx} className="p-2 bg-white/10 rounded truncate">
                  {msg.from_user_id === currentUser.id ? 'You: ' : ''}{normalizeText(msg.message)}
                </div>
              ))}
            </div>
          )}
          <button
            onClick={onMinimize}
            className="w-full mt-2 px-3 py-2 bg-purple-500/30 hover:bg-purple-500/50 rounded-lg text-xs font-medium transition-colors flex-shrink-0"
          >
            Open Full Chat
          </button>
        </div>
      </div>
    );
  }

  // Full messaging panel
  return (
    <div
      ref={panelRef}
      style={isMobile ? {
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
      } : { 
        left: position.x, 
        top: position.y,
        width: size.width,
        height: size.height
      }}
      className={`fixed z-[1100] ${isMobile ? 'bg-gradient-to-b from-slate-900 to-purple-900' : 'bg-gradient-to-br from-slate-900 via-purple-900/40 to-slate-900 backdrop-blur-xl border border-purple-500/30 rounded-2xl shadow-2xl'} flex flex-col overflow-hidden`}
    >
      {/* Resize Handles - DESKTOP ONLY */}
      {!isMobile && (
        <>
          <div 
            onMouseDown={(e) => handleResizeStart(e, 'n')}
            className="absolute top-0 left-2 right-2 h-2 cursor-ns-resize hover:bg-purple-500/20 transition-colors"
          />
          <div 
            onMouseDown={(e) => handleResizeStart(e, 's')}
            className="absolute bottom-0 left-2 right-2 h-2 cursor-ns-resize hover:bg-purple-500/20 transition-colors"
          />
          <div 
            onMouseDown={(e) => handleResizeStart(e, 'w')}
            className="absolute top-2 bottom-2 left-0 w-2 cursor-ew-resize hover:bg-purple-500/20 transition-colors"
          />
          <div 
            onMouseDown={(e) => handleResizeStart(e, 'e')}
            className="absolute top-2 bottom-2 right-0 w-2 cursor-ew-resize hover:bg-purple-500/20 transition-colors"
          />
          {/* Corner Handles */}
          <div 
            onMouseDown={(e) => handleResizeStart(e, 'nw')}
            className="absolute top-0 left-0 w-4 h-4 cursor-nwse-resize hover:bg-purple-500/30 transition-colors rounded-tl-2xl"
          />
          <div 
            onMouseDown={(e) => handleResizeStart(e, 'ne')}
            className="absolute top-0 right-0 w-4 h-4 cursor-nesw-resize hover:bg-purple-500/30 transition-colors rounded-tr-2xl"
          />
          <div 
            onMouseDown={(e) => handleResizeStart(e, 'sw')}
            className="absolute bottom-0 left-0 w-4 h-4 cursor-nesw-resize hover:bg-purple-500/30 transition-colors rounded-bl-2xl"
          />
          <div 
            onMouseDown={(e) => handleResizeStart(e, 'se')}
            className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize hover:bg-purple-500/30 transition-colors rounded-br-2xl"
          />
        </>
      )}

      {/* Header - DESKTOP ONLY drag handle */}
      <div
        onMouseDown={isMobile ? undefined : handleDragStart}
        className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-purple-600 to-indigo-600 cursor-move select-none"
      >
        <div className="flex items-center gap-3">
          <GripVertical className="w-5 h-5 text-white/70" />
          <MessageCircle className="w-6 h-6 text-white" />
          <div>
            <h2 className="font-bold text-lg text-white">WHIPT Messages</h2>
            <p className="text-xs text-white/70">Cross-browser messaging enabled</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onMinimize}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Minimize"
          >
            <MinusCircle className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Join Request Notifications */}
        {joinRequests.length > 0 && (
          <div className="absolute left-4 right-4 top-4 z-40">
            <div className="bg-emerald-500/15 border border-emerald-400/40 rounded-2xl p-4 shadow-lg backdrop-blur-md">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 text-emerald-100">
                  <Users className="w-5 h-5" />
                  <span className="font-semibold">Group Join Requests</span>
                  <span className="text-xs font-bold bg-emerald-500 text-white px-2 py-0.5 rounded-full">
                    {joinRequests.length}
                  </span>
                </div>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {joinRequests.map(request => {
                  const group = groups.find(g => g.id === request.group_id);
                  return (
                    <div key={request.id} className="bg-white/10 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-white">{request.user_name}</p>
                        <p className="text-xs text-emerald-200">wants to join "{group?.name}"</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            const success = await GroupChatAPI.approveJoinRequest(request.id, request.group_id, request.user_id);
                            if (success) {
                              setJoinRequests(prev => prev.filter(r => r.id !== request.id));
                              // Reload groups to update member count
                              const updated = await GroupChatAPI.getUserGroups(currentUser.id);
                              setGroups(updated);
                            }
                          }}
                          className="px-3 py-1 text-xs font-bold rounded-full bg-emerald-500 hover:bg-emerald-600 text-white transition-colors"
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={async () => {
                            await GroupChatAPI.rejectJoinRequest(request.id);
                            setJoinRequests(prev => prev.filter(r => r.id !== request.id));
                          }}
                          className="px-3 py-1 text-xs font-bold rounded-full bg-red-500/30 hover:bg-red-500/50 text-red-200 transition-colors"
                        >
                          ✗ Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        
        {/* Admin Notices - REMOVED (no longer needed) */}

        {/* Left Sidebar - User List (Hide on mobile when chat is open) */}
        <div className={`${isMobile ? 'w-full' : 'w-80 border-r border-white/10'} flex flex-col ${isMobile ? 'bg-transparent' : 'bg-black/20'} ${isMobile && showChatOnMobile ? 'hidden' : 'flex'}`}>
          {/* Tabs - VERTICAL STACK on Mobile, Horizontal on Desktop */}
          <div className={`${isMobile ? 'flex flex-col' : 'flex'} border-b border-white/10`}>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`${isMobile ? 'w-full py-4' : 'flex-1 py-3'} px-4 font-bold ${isMobile ? 'text-base' : 'text-xs'} transition-colors relative ${
                activeTab === 'contacts'
                  ? 'bg-purple-600 text-white'
                  : 'bg-transparent text-slate-400 hover:bg-white/5'
              }`}
            >
              <MessageCircle className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} inline mr-2`} />
              Contacts
              {contacts.length > 0 && (
                <span className={`ml-1 ${isMobile ? 'text-xs' : 'text-[10px]'} opacity-70`}>({contacts.length})</span>
              )}
              {incomingRequests.length > 0 && (
                <span className={`absolute top-${isMobile ? '2' : '1'} right-${isMobile ? '2' : '1'} bg-red-500 text-white ${isMobile ? 'text-xs w-6 h-6' : 'text-[10px] w-5 h-5'} rounded-full flex items-center justify-center animate-pulse`}>
                  {incomingRequests.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('groups')}
              className={`${isMobile ? 'w-full py-4' : 'flex-1 py-3'} px-4 font-bold ${isMobile ? 'text-base' : 'text-xs'} transition-colors ${
                activeTab === 'groups'
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white'
                  : 'bg-transparent text-slate-400 hover:bg-white/5'
              }`}
            >
              <Users className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} inline mr-2`} />
              Groups
              {groups.length > 0 && (
                <span className={`ml-1 ${isMobile ? 'text-xs' : 'text-[10px]'} opacity-70`}>({groups.length})</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('browse')}
              className={`${isMobile ? 'w-full py-4' : 'flex-1 py-3'} px-4 font-bold ${isMobile ? 'text-base' : 'text-xs'} transition-colors ${
                activeTab === 'browse'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-transparent text-slate-400 hover:bg-white/5'
              }`}
            >
              <UserPlus className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} inline mr-2`} />
              Browse
            </button>
          </div>

          {/* Helper Text - Only show on mobile when no selection */}
          {isMobile && !selectedUser && !selectedGroup && (
            <div className="p-4 text-center border-b border-white/10 bg-gradient-to-r from-purple-500/10 to-indigo-500/10">
              <p className="text-base font-medium text-white">
                {activeTab === 'contacts' && '👤 Select a contact to start chatting'}
                {activeTab === 'groups' && '👥 Select a group to join the conversation'}
                {activeTab === 'browse' && '🔍 Browse and connect with users'}
              </p>
            </div>
          )}

          {/* Search */}
          <div className="p-3 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-10 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-purple-400"
              />
            </div>
          </div>

          {/* User List */}
          <div className="flex-1 overflow-y-auto">
            {/* CONTACTS TAB - Approved contacts only */}
            {activeTab === 'contacts' && (
              <div className="h-full flex flex-col">
                {/* Incoming Requests Notification */}
                {incomingRequests.length > 0 && (
                  <div className="p-2 bg-red-500/20 border-b border-red-500/30">
                    <p className="text-xs font-bold text-red-200 flex items-center gap-2">
                      <Bell className="w-4 h-4 animate-pulse" />
                      {incomingRequests.length} Contact Request{incomingRequests.length > 1 ? 's' : ''}
                    </p>
                    {incomingRequests.map(req => (
                      <div key={req.id} className="mt-2 p-2 bg-black/30 rounded-lg">
                        <p className="text-xs text-white mb-1"><strong>{req.from_user_name}</strong> wants to connect</p>
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              const success = await ContactRequestAPI.approveContactRequest(req.id);
                              if (success) {
                                setIncomingRequests(prev => prev.filter(r => r.id !== req.id));
                                // Reload contacts
                                const updated = await ContactRequestAPI.getApprovedContacts(currentUser.id);
                                setContacts(updated as any);
                              }
                            }}
                            className="flex-1 px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded flex items-center justify-center gap-1"
                          >
                            <Check className="w-3 h-3" /> Accept
                          </button>
                          <button
                            onClick={async () => {
                              const success = await ContactRequestAPI.rejectContactRequest(req.id);
                              if (success) {
                                setIncomingRequests(prev => prev.filter(r => r.id !== req.id));
                              }
                            }}
                            className="flex-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded flex items-center justify-center gap-1"
                          >
                            <XCircle className="w-3 h-3" /> Decline
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Contacts List */}
                <div className="flex-1 overflow-y-auto p-2">
                  {contacts.length === 0 ? (
                    <div className="text-center py-12 text-slate-400 text-sm">
                      <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No contacts yet</p>
                      <p className="text-xs mt-2 px-4">Go to Browse tab to add contacts!</p>
                    </div>
                  ) : (
                    contacts.map((user: any) => (
                      <button
                        key={user.id}
                        onClick={() => setSelectedUser(user)}
                        className={`group w-full px-3 py-2 rounded-lg mb-1 transition-all ${
                          selectedUser?.id === user.id
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
                            : 'hover:bg-white/5 text-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            {user.avatar ? (
                              <img 
                                src={user.avatar}
                                alt={user.name}
                                className="w-10 h-10 rounded-full object-cover ring-2 ring-purple-400"
                              />
                            ) : (
                              <div className={`w-10 h-10 rounded-full ${user.profile_color || 'bg-gradient-to-br from-purple-400 to-indigo-500'} flex items-center justify-center text-white font-bold`}>
                                {user.name[0].toUpperCase()}
                              </div>
                            )}
                            {unreadCounts[user.id] && (
                              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold animate-pulse">
                                {unreadCounts[user.id]}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-bold text-sm">{user.name}</p>
                            <p className="text-xs text-emerald-400">● Contact</p>
                          </div>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (confirm(`Unfriend ${user.name}?`)) {
                                const success = await ContactRequestAPI.removeContact(currentUser.id, user.id);
                                if (success) {
                                  setContacts(prev => prev.filter((c: any) => c.id !== user.id));
                                }
                              }
                            }}
                            className="opacity-0 group-hover:opacity-100 px-3 py-1 bg-red-500/20 hover:bg-red-500/30 rounded text-xs font-bold text-red-300 hover:text-red-200 transition-all"
                          >
                            Unfriend
                          </button>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* BROWSE TAB - All users with request buttons */}
            {activeTab === 'browse' && (
              <div className="h-full flex flex-col p-2">
                <div className="mb-2 text-xs text-slate-400 px-2">
                  Browse all users • Click to send contact request
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {allUsers.length === 0 ? (
                    <div className="text-center py-12 text-slate-400 text-sm">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No users available</p>
                    </div>
                  ) : (
                    allUsers.map(user => (
                      <div
                        key={user.id}
                        className="px-3 py-2 rounded-lg mb-1 bg-white/5 hover:bg-white/10 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          {user.avatar ? (
                            <img 
                              src={user.avatar}
                              alt={user.name}
                              className="w-10 h-10 rounded-full object-cover ring-2 ring-slate-600"
                            />
                          ) : (
                            <div className={`w-10 h-10 rounded-full ${user.profile_color || 'bg-gradient-to-br from-slate-500 to-slate-700'} flex items-center justify-center text-white font-bold`}>
                              {user.name[0].toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 text-left">
                            <p className="font-bold text-sm text-white">{user.name}</p>
                            <p className="text-xs text-slate-400">{user.email}</p>
                          </div>
                          {user.request_status === 'approved' ? (
                            <span className="text-xs text-emerald-400 font-bold">✓ Contact</span>
                          ) : user.request_status === 'pending' ? (
                            <span className="text-xs text-yellow-400 font-bold">⏳ Pending</span>
                          ) : (
                            <button
                              onClick={async () => {
                                const success = await ContactRequestAPI.sendContactRequest(
                                  currentUser.id,
                                  user.id,
                                  currentUser.name,
                                  user.name
                                );
                                if (success) {
                                  alert(`Contact request sent to ${user.name}!`);
                                  // Reload users to update status
                                  const updated = await ContactRequestAPI.getAllUsers(currentUser.id);
                                  setAllUsers(updated);
                                }
                              }}
                              className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded font-bold flex items-center gap-1"
                            >
                              <UserPlus className="w-3 h-3" /> Add
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* GROUPS TAB - Unchanged */}
            {activeTab === 'groups' && (
              <div className="flex flex-col h-full">
                <div className="p-4 border-b border-white/10 space-y-2">
                  <button
                    onClick={() => setShowCreateGroupModal(true)}
                    className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold hover:from-purple-700 hover:to-indigo-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Users className="w-5 h-5" />
                    Create New Group
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-2 overscroll-contain">
                  {groups.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No groups available</p>
                      <p className="text-xs mt-1 px-4">All public groups will appear here. Create one above!</p>
                    </div>
                  ) : (
                    groups.map(group => {
                      // Debug logging
                      console.log('[Groups Debug]', group.name, {
                        user_is_member: group.user_is_member,
                        has_pending_request: group.has_pending_request,
                        member_count: group.member_count,
                        created_by: group.created_by,
                        current_user: currentUser.id
                      });
                      
                      return (
                      <button
                        key={group.id}
                        onClick={() => {
                          if (group.user_is_member) {
                            setSelectedGroup(group);
                            setSelectedUser(null);
                          }
                        }}
                        className={`w-full px-3 py-2 rounded-lg mb-1 transition-all ${
                          selectedGroup?.id === group.id
                            ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white'
                            : 'hover:bg-white/5 text-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold relative">
                            <Users className="w-5 h-5" />
                            {group.created_by === currentUser.id && (
                              <span className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center text-[10px]" title="You created this group">
                                👑
                              </span>
                            )}
                          </div>
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-sm">{group.name}</p>
                              {group.created_by === currentUser.id && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/30 text-purple-200 font-bold">
                                  Creator
                                </span>
                              )}
                            </div>
                            <p className="text-xs opacity-70">
                              {group.member_count} member{group.member_count !== 1 ? 's' : ''}
                              {group.user_is_member && ' • You\'re a member'}
                            </p>
                          </div>
                          {!group.user_is_member && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (group.has_pending_request) {
                                  return; // Already requested
                                }
                                const success = await GroupChatAPI.requestJoinGroup(group.id, currentUser.id, currentUser.name);
                                if (success) {
                                  alert('Join request sent! Waiting for approval.');
                                  // Reload groups to update status
                                  const updated = await GroupChatAPI.getUserGroups(currentUser.id);
                                  setGroups(updated);
                                }
                              }}
                              disabled={group.has_pending_request}
                              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                                group.has_pending_request
                                  ? 'bg-yellow-600/30 text-yellow-300 cursor-not-allowed'
                                  : 'bg-emerald-600/30 hover:bg-emerald-600/50 text-emerald-300'
                              }`}
                            >
                              {group.has_pending_request ? '⏳ Pending' : '+ Join'}
                            </button>
                          )}
                          {group.user_is_member && (
                            <span className="text-xs text-emerald-400 font-bold">✓ Joined</span>
                          )}
                        </div>
                      </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Chat Area (HIDE on mobile when no chat selected) */}
        <div className={`flex-1 flex flex-col ${isMobile && !selectedUser && !selectedGroup ? 'hidden' : 'flex'}`}>
          {selectedUser || selectedGroup ? (
            <>
              {/* Chat Header */}
              <div className="px-4 py-3 border-b border-white/10 bg-gradient-to-r from-purple-600/20 to-indigo-600/20">
                {selectedUser && (
                  <div className="flex items-center gap-3">
                    {isMobile && (
                      <button
                        onClick={() => {
                          setShowChatOnMobile(false);
                          setSelectedUser(null);
                          setSelectedGroup(null);
                        }}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        title="Back to contacts"
                      >
                        <ArrowLeft className="w-5 h-5 text-white" />
                      </button>
                    )}
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center text-white font-bold ring-2 ring-purple-400">
                      {selectedUser.name[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-bold text-white text-base">{selectedUser.name}</p>
                      <p className={`text-xs ${selectedUser.show_online_status === true || selectedUser.show_online_status === 1 ? 'text-emerald-400' : 'text-slate-400'}`}>
                        ● {selectedUser.show_online_status === true || selectedUser.show_online_status === 1 ? 'Online' : 'Offline'}
                      </p>
                    </div>
                  </div>
                )}
                {selectedGroup && (
                  <div className="flex items-center gap-3">
                    {isMobile && (
                      <button
                        onClick={() => {
                          setShowChatOnMobile(false);
                          setSelectedUser(null);
                          setSelectedGroup(null);
                        }}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        title="Back to groups"
                      >
                        <ArrowLeft className="w-5 h-5 text-white" />
                      </button>
                    )}
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold ring-2 ring-orange-400">
                      <Users className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-white text-base">{selectedGroup.name}</p>
                        {selectedGroup.created_by === currentUser.id && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/40 text-purple-200 font-bold">
                            👑 You're the Creator
                          </span>
                        )}
                      </div>
                      <div className="group/tooltip relative">
                        <p className="text-xs text-slate-400 cursor-help">
                          {selectedGroup.member_count} members: {groupMembers.slice(0, 3).map(m => m.user_name).join(', ')}{groupMembers.length > 3 ? '...' : ''}
                        </p>
                        {groupMembers.length > 0 && (
                          <div className="absolute left-0 top-full mt-2 hidden group-hover/tooltip:block z-50 bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-2xl min-w-[260px] max-w-[320px]">
                            <p className="text-xs font-bold text-white mb-2">All Members:</p>
                            <div className="space-y-1">
                              {groupMembers.map(m => (
                                <div key={m.id} className="flex items-center justify-between text-xs gap-2">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <span className="text-slate-300 truncate">• {m.user_name}</span>
                                    {m.user_id === selectedGroup.created_by && (
                                      <span className="text-purple-400 text-[10px] font-bold whitespace-nowrap">👑 Creator</span>
                                    )}
                                    {m.user_id === currentUser.id && m.user_id !== selectedGroup.created_by && (
                                      <span className="text-emerald-400 text-[10px] font-bold whitespace-nowrap">You</span>
                                    )}
                                  </div>
                                  {selectedGroup.created_by === currentUser.id && m.user_id !== currentUser.id && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (confirm(`Remove ${m.user_name} from group?`)) {
                                          const removed = await GroupChatAPI.removeMemberFromGroup(selectedGroup.id, currentUser.id, m.user_id);
                                          if (removed) {
                                            // Reload members
                                            const updatedMembers = await GroupChatAPI.getGroupMembers(selectedGroup.id);
                                            setGroupMembers(updatedMembers);
                                            // Reload groups to update count
                                            const updatedGroups = await GroupChatAPI.getUserGroups(currentUser.id);
                                            setGroups(updatedGroups);
                                          }
                                        }
                                      }}
                                      className="p-1 hover:bg-red-500/20 rounded transition-colors text-red-400 hover:text-red-300 flex-shrink-0"
                                      title="Remove member"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedGroup.created_by === currentUser.id && (
                      <button
                        onClick={async () => {
                          if (confirm(`Delete group "${selectedGroup.name}"?`)) {
                            const deleted = await GroupChatAPI.deleteGroup(selectedGroup.id, currentUser.id);
                            if (deleted) {
                              setGroups(prev => prev.filter(g => g.id !== selectedGroup.id));
                              setSelectedGroup(null);
                            }
                          }
                        }}
                        className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-400 hover:text-red-300"
                        title="Delete group"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                {selectedUser && messages.length === 0 && (
                  <div className="text-center text-slate-400 text-sm py-8">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No messages yet</p>
                    <p className="text-xs mt-1">Start the conversation with {selectedUser.name}!</p>
                  </div>
                )}
                {selectedUser && messages.map((msg) => {
                  const isMe = msg.from_user_id === currentUser.id;
                  
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                          isMe
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
                            : 'bg-slate-700 text-white'
                        }`}
                      >
                        <p className="text-xs font-bold mb-1 opacity-70">
                          {isMe ? 'You' : selectedUser.name}
                        </p>
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                        <p className="text-[10px] mt-1 opacity-50">
                          {new Date(msg.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
                {selectedGroup && groupMessages.length === 0 && (
                  <div className="text-center text-slate-400 text-sm py-8">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No messages yet</p>
                    <p className="text-xs mt-1">Start the conversation in {selectedGroup.name}!</p>
                  </div>
                )}
                {selectedGroup && groupMessages.map((msg) => {
                  const isMe = msg.user_id === currentUser.id;
                  
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                          isMe
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
                            : 'bg-slate-700 text-white'
                        }`}
                      >
                        <p className="text-xs font-bold mb-1 opacity-70">
                          {msg.user_name}
                        </p>
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                        <p className="text-[10px] mt-1 opacity-50">
                          {new Date(msg.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-white/10 bg-black/20 sticky bottom-0">
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!newMessage.trim()) return;

                    if (selectedUser) {
                      const result = await MessagingAPI.sendMessage(currentUser.id, selectedUser.id, newMessage.trim());
                      if (result && 'message' in result && result.message) {
                        // API returns { success, message } — push normalized message payload only
                        setMessages(prev => [...prev, result.message]);
                        setNewMessage('');
                        // Reset textarea height
                        const textarea = e.currentTarget.querySelector('textarea');
                        if (textarea) textarea.style.height = 'auto';
                        // Auto-scroll to bottom after sending
                        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                      }
                    } else if (selectedGroup) {
                      const message = await GroupChatAPI.sendGroupMessage(selectedGroup.id, currentUser.id, newMessage.trim());
                      if (message) {
                        setGroupMessages(prev => [...prev, message]);
                        setNewMessage('');
                        // Reset textarea height
                        const textarea = e.currentTarget.querySelector('textarea');
                        if (textarea) textarea.style.height = 'auto';
                        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                      }
                    }
                  }}
                  className="space-y-2"
                >
                  {/* Attachment Buttons Row */}
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          alert('File upload feature coming soon!');
                          e.target.value = ''; // Reset
                        }
                      }}
                    />
                    <label
                      htmlFor="file-upload"
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
                      title="Attach file"
                    >
                      <Paperclip className="w-5 h-5 text-purple-300" />
                    </label>
                    
                    <input
                      type="file"
                      id="image-upload"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          alert('Image upload feature coming soon!');
                          e.target.value = ''; // Reset
                        }
                      }}
                    />
                    <label
                      htmlFor="image-upload"
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
                      title="Send image"
                    >
                      <ImageIcon className="w-5 h-5 text-pink-300" />
                    </label>
                    
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        title="Add emoji"
                      >
                        <Smile className="w-5 h-5 text-yellow-300" />
                      </button>
                      
                      {showEmojiPicker && (
                        <div className="absolute bottom-12 left-0 z-50 w-64 rounded-xl border border-white/15 bg-slate-900/95 shadow-2xl p-3">
                          <div className="grid grid-cols-8 gap-1">
                            {INLINE_EMOJIS.map((emoji) => (
                              <button
                                key={emoji}
                                type="button"
                                onClick={() => {
                                  setNewMessage(prev => prev + emoji);
                                  setShowEmojiPicker(false);
                                }}
                                className="h-7 w-7 rounded hover:bg-white/10 text-base leading-none"
                                aria-label={`Insert ${emoji}`}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <span className="text-xs text-slate-500 ml-auto">
                      {newMessage.length > 0 && `${newMessage.length} characters`}
                    </span>
                  </div>
                  
                  {/* Message Input Row */}
                  <div className="flex gap-2 items-end">
                    <textarea
                      ref={(el) => {
                        // Ensure textarea is always focusable on mobile
                        if (el) {
                          el.style.touchAction = 'manipulation';
                          el.style.webkitUserSelect = 'text';
                          el.style.userSelect = 'text';
                        }
                      }}
                      rows={1}
                      inputMode="text"
                      autoComplete="off"
                      autoCorrect="on"
                      autoCapitalize="sentences"
                      spellCheck="true"
                      enterKeyHint="send"
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        // Auto-resize textarea
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                      onTouchStart={(e) => {
                        // CRITICAL: Stop propagation but DO NOT prevent default
                        // This allows keyboard to open
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        // Ensure focus on mobile
                        e.stopPropagation();
                        e.currentTarget.focus();
                      }}
                      onFocus={(e) => {
                        // Scroll into view when keyboard appears
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 300);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          e.currentTarget.form?.requestSubmit();
                        }
                      }}
                      placeholder="Type a message..."
                      className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-purple-400 text-base resize-none max-h-32 overflow-y-auto touch-manipulation"
                      style={{ 
                        fontSize: '16px', 
                        minHeight: '42px',
                        touchAction: 'manipulation',
                        WebkitUserSelect: 'text', 
                        userSelect: 'text',
                        WebkitTapHighlightColor: 'transparent'
                      }}
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="px-6 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-bold">Select a user or group to start chatting</p>
                <p className="text-sm mt-2">Choose someone from the list on the left</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Group Modal */}
      {showCreateGroupModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-slate-900 via-purple-900/40 to-slate-900 border border-purple-500/30 rounded-2xl p-6 w-[500px] max-w-[90vw] shadow-2xl">
            <h3 className="text-2xl font-bold text-white mb-4">Create New Group</h3>
            
            <div className="space-y-4">
              {/* Group Name Input */}
              <div>
                <label className="text-white text-sm font-bold mb-2 block">Group Name</label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Enter group name..."
                  className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-purple-400"
                />
              </div>

              {/* Member Selection */}
              <div>
                <label className="text-white text-sm font-bold mb-2 block">Select Members (Optional)</label>
                <div className="max-h-48 overflow-y-auto space-y-2 border border-white/10 rounded-xl p-3 bg-white/5">
                  {onlineUsers.map(user => (
                    <label key={user.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(user.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMembers(prev => [...prev, user.id]);
                          } else {
                            setSelectedMembers(prev => prev.filter(id => id !== user.id));
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center text-white font-bold text-sm">
                        {user.name[0].toUpperCase()}
                      </div>
                      <span className="text-white text-sm">{user.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">{selectedMembers.length} member(s) selected</p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateGroupModal(false);
                  setGroupName('');
                  setSelectedMembers([]);
                }}
                className="flex-1 px-4 py-2 rounded-xl bg-slate-700 text-white hover:bg-slate-600 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (groupName.trim()) {
                    const group = await GroupChatAPI.createGroup(groupName, currentUser.id, selectedMembers);
                    if (group) {
                      console.log('[Messaging] Group created:', group);
                      setGroups(prev => [group, ...prev]);
                      
                      // Show notification to creator
                      if (selectedMembers.length > 0) {
                        const memberNames = onlineUsers
                          .filter(u => selectedMembers.includes(u.id))
                          .map(u => u.name)
                          .join(', ');
                        alert(`🎉 Group "${groupName}" created!\n\nMembers: ${memberNames}\n\nThey will see this group in their Groups tab.`);
                      } else {
                        alert(`🎉 Group "${groupName}" created!\n\nYou can add members later.`);
                      }
                      
                      setShowCreateGroupModal(false);
                      setGroupName('');
                      setSelectedMembers([]);
                      setActiveTab('groups'); // Stay on groups tab
                    } else {
                      alert('Failed to create group');
                    }
                  } else {
                    alert('Please enter a group name');
                  }
                }}
                disabled={!groupName.trim()}
                className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Create Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddContactModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[20000] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-2 border-green-400/60 rounded-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 border-b border-green-400/30 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold text-green-100">Add Contact</h3>
                <button
                  onClick={() => {
                    setShowAddContactModal(false);
                    setAddContactSearch('');
                  }}
                  className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-green-200/80">Add people to your contacts list</p>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-white/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={addContactSearch}
                  onChange={(e) => setAddContactSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400 focus:outline-none focus:border-green-400"
                />
              </div>
            </div>

            {/* Available Users List */}
            <div className="p-4 max-h-96 overflow-y-auto">
              {availableUsers.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No users available to add</p>
                  <p className="text-xs mt-1">You've added everyone!</p>
                </div>
              ) : (
                availableUsers
                  .filter(user => 
                    user.name.toLowerCase().includes(addContactSearch.toLowerCase()) ||
                    user.email.toLowerCase().includes(addContactSearch.toLowerCase())
                  )
                  .map(user => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 mb-2"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center text-white font-bold">
                          {user.name[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-white text-sm">{user.name}</p>
                          <p className="text-xs text-slate-400">{user.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddContact(user.id)}
                        className="px-3 py-1.5 rounded-lg bg-green-600/20 hover:bg-green-600/30 text-green-200 text-sm font-semibold transition-all border border-green-400/30"
                      >
                        Add
                      </button>
                    </div>
                  ))
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 p-4">
              <button
                onClick={() => {
                  setShowAddContactModal(false);
                  setAddContactSearch('');
                }}
                className="w-full px-4 py-2 rounded-lg bg-white/10 text-slate-300 hover:bg-white/20 text-sm transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Browse Groups Modal */}
      {showBrowseGroupsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[20000] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-2 border-emerald-400/60 rounded-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600/20 to-teal-600/20 border-b border-emerald-400/30 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold text-emerald-100">Browse Groups</h3>
                <button
                  onClick={() => setShowBrowseGroupsModal(false)}
                  className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-emerald-200/80">Join existing groups or create your own</p>
            </div>

            {/* Groups List */}
            <div className="p-4 max-h-96 overflow-y-auto">
              {allGroups.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No groups available yet</p>
                  <p className="text-xs mt-1">Be the first to create one!</p>
                </div>
              ) : (
                allGroups.map(group => {
                  const isMember = groups.some(g => g.id === group.id);
                  return (
                    <div
                      key={group.id}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 mb-2 border border-white/10"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold">
                          <Users className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-white text-sm">{group.name}</p>
                          <p className="text-xs text-slate-400">{group.member_count} members</p>
                        </div>
                      </div>
                      {isMember ? (
                        <span className="px-3 py-1.5 rounded-lg bg-emerald-600/20 text-emerald-200 text-xs font-semibold border border-emerald-400/30">
                          ✓ Joined
                        </span>
                      ) : (
                        <button
                          onClick={async () => {
                            const success = await GroupChatAPI.addGroupMember(group.id, currentUser.id);
                            if (success) {
                              alert(`✅ Joined "${group.name}"!`);
                              // Reload user's groups
                              const userGroups = await GroupChatAPI.getUserGroups(currentUser.id);
                              setGroups(userGroups);
                              setShowBrowseGroupsModal(false);
                            } else {
                              alert('Failed to join group');
                            }
                          }}
                          className="px-3 py-1.5 rounded-lg bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-200 text-sm font-semibold transition-all border border-emerald-400/30"
                        >
                          Join
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 p-4">
              <button
                onClick={() => setShowBrowseGroupsModal(false)}
                className="w-full px-4 py-2 rounded-lg bg-white/10 text-slate-300 hover:bg-white/20 text-sm transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
